import { ButtonStyle } from "@components/Atoms/Button/ButtonStyle";
import Notification from "@components/Atoms/Notification";
import SpinnerLoader from "@components/Atoms/SpinnerLoader";
import NavigationTabsService from "@components/Layouts/LeftNavigationTabs/NavigationTabsService";
import { LeftNavigationTab } from "@components/Layouts/StatusBar/Extensions/ArticleStatusBar/ArticleStatusBar";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
// biome-ignore lint/style/noRestrictedImports: will be deleted with new sidebar UI
import styled from "@emotion/styled";
import SyncService from "@ext/git/actions/Sync/logic/SyncService";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";

const NotificationWrapper = styled.div`
	position: relative;
	margin-left: 0.5em;

	> div {
		position: static !important;
		font-size: 10px !important;
		> div {
			padding: 1px;
		}
	}
`;

const ButtonContentWrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Spinner = styled(SpinnerLoader)`
	margin-right: 0.5em;
`;

const CreateBranchTooltipContent = styled.div`
	text-align: center;
`;

/**
 * The legacy Button rendered its clickable node as a <div>, which cannot carry the button role, the
 * disabled state or keyboard activation. This is the same look on a real <button>: the base is the
 * legacy `.content` rule, each class below is the matching legacy `buttonStyle` rule.
 */
const PublishButton = styled.button`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0.33em 0.88em;
	appearance: none;
	margin: 0;
	border: none;
	border-radius: 0;
	background: none;
	color: inherit;
	font: inherit;
	font-weight: var(--font-weight-default);
	text-align: inherit;
	cursor: pointer;
	user-select: none;

	&:disabled {
		pointer-events: none;
		opacity: 0.4;
	}

	&.default {
		border-radius: var(--radius-medium);
		background: var(--color-btn-default-bg);
		color: var(--color-btn-default-text);
		border: var(--border-btn-default);

		&:hover {
			opacity: var(--opacity-btn-default-bg-hover);
			color: var(--color-btn-default-text-hover);
			background: var(--color-btn-default-bg-hover);
		}
	}

	&.orange {
		background: var(--color-btn-bg);
		color: var(--color-text-accent);
		border: 1px solid var(--color-text-accent);
		border-radius: var(--radius-medium);

		&:hover {
			color: white;
			background: var(--color-text-accent);
		}
	}

	&.underline {
		border-radius: var(--radius-medium);
		color: var(--color-btn-underline-text);
		font-weight: var(--font-weight-btn-underline);
		padding: var(--padding-btn-underline);
		margin: var(--margin-btn-underline);
		text-decoration: none;

		&:hover {
			color: var(--color-btn-underline-text-hover);
			text-decoration: var(--text-decoration-btn-underline-hover);
			background-color: var(--color-btn-underline-bg-hover);
		}
	}

	&.transparent {
		color: var(--color-primary-general);
		font-weight: var(--font-weight-default);
		padding: 0;

		&:hover {
			color: var(--color-primary);
		}
	}

	&.transparentInverse {
		color: var(--color-primary-general-inverse);
		font-weight: var(--font-weight-default);
		padding: 0;

		&:hover {
			color: var(--color-primary-inverse);
		}
	}

	&.purple {
		background: var(--merger-bottom-primary);
		color: white;
		border-radius: var(--radius-medium);

		&:hover {
			color: #d338f8;
			background: white;
		}
	}

	&.git {
		background: white;
		color: var(--version-control-primary);
		border-radius: var(--radius-medium);

		&:hover {
			background: var(--version-control-primary);
			color: white;
		}
	}
`;

interface PublishActionButtonProps {
	showCreateBranchButton?: boolean;
	showGitConflictsButton?: boolean;
	publishButtonTooltip?: string;
	disablePublishButton: boolean;
	canPush: boolean;
	fileCount: number;
	isLoading?: boolean;
	buttonStyle?: ButtonStyle;
	onPublishClick: () => Promise<void> | void;
}

const PublishActionButton = (props: PublishActionButtonProps) => {
	const {
		showCreateBranchButton,
		showGitConflictsButton,
		publishButtonTooltip,
		disablePublishButton,
		canPush,
		fileCount,
		isLoading,
		buttonStyle,
		onPublishClick,
	} = props;

	const apiUrlCreator = ApiUrlCreatorService.value;

	if (showCreateBranchButton) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<div>
						<Button
							onClick={() => NavigationTabsService.setBottom(LeftNavigationTab.CreateBranch)}
							size="sm"
							startIcon={"plus"}
						>
							{t("add-new-branch")}
						</Button>
					</div>
				</TooltipTrigger>
				<TooltipContent>
					<CreateBranchTooltipContent>
						<div>{t("git.publish.error.direct-publish-forbidden")}</div>
						<div>{t("git.publish.error.create-branch-and-merge")}</div>
					</CreateBranchTooltipContent>
				</TooltipContent>
			</Tooltip>
		);
	}

	if (showGitConflictsButton) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<div>
						<Button
							onClick={async () => {
								await SyncService.sync(apiUrlCreator, true);
							}}
							size="sm"
						>
							{t("git.publish.error.resolve-conflicts")}
						</Button>
					</div>
				</TooltipTrigger>
				<TooltipContent>
					<CreateBranchTooltipContent>
						<div>{t("git.publish.error.has-conflicts-description")}</div>
					</CreateBranchTooltipContent>
				</TooltipContent>
			</Tooltip>
		);
	}

	const isPublishDisabled = disablePublishButton || !canPush;

	// The wrapper keeps receiving pointer events while the button is disabled, so the tooltip still opens.
	const publishButton = (
		<div>
			<PublishButton
				className={buttonStyle ?? ButtonStyle.default}
				disabled={isPublishDisabled}
				onClick={onPublishClick}
				type="button"
			>
				<ButtonContentWrapper>
					{isLoading && <Spinner height={12} width={12} />}
					{t("git.publish.to-publish")}
					{fileCount > 0 && (
						<NotificationWrapper className="file-count-notification">
							<Notification>{fileCount}</Notification>
						</NotificationWrapper>
					)}
				</ButtonContentWrapper>
			</PublishButton>
		</div>
	);

	if (publishButtonTooltip) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>{publishButton}</TooltipTrigger>
				<TooltipContent>{publishButtonTooltip}</TooltipContent>
			</Tooltip>
		);
	}

	return publishButton;
};

export default PublishActionButton;
